import { makeAutoObservable } from 'mobx';
import { injectable, inject, symbolToken } from '@servicetitan/react-ioc';
import { v4 as uuIdV4 } from 'uuid';
import {
  CreatedBy,
  CreatedByType,
  CreateInteractionRequest,
  CreateMessageRequest,
  InteractionApi,
  Message,
  MessageType,
  SimpleMessage,
  Interaction,
} from '../api/generated/aisInteraction.api';
import {
  HashRegex,
  logger,
  MANAGER_ASSIST_LS_ACCEPTED,
  MANAGER_ASSIST_LS_KEY,
} from '../utils/utils';

export const USER_ID_ChatStore = symbolToken<string>('USER_ID', false);

@injectable()
export class ManagerAssistChatStore {
  assistantId = 'ccpro-manager-assist';
  targetRoleId = 'none';
  isLoading = false;
  errorMessage: string | null = null;
  enableRetry = false;
  messages: Message[] = [];
  bannerAccepted = false;
  currentInteraction: Interaction | null = null;
  suggestions = ['Total calls today', 'Average response time today'];

  // For message polling
  pollInterval = 3000; // 3 seconds
  pollMaxRetries = 7; // max 7 retries i.e. 21 seconds
  private pollTimer: NodeJS.Timeout | null = null;
  private initialMessageCount = 0;
  private pollRetryCount = 0;

  constructor(
    @inject(InteractionApi) private readonly interactionApi: InteractionApi,
    @inject(USER_ID_ChatStore) private readonly userId: string = '0',
  ) {
    makeAutoObservable(this);

    const firstMessage = this.addMessage(
      'Ask me anything related to efficiency and booking metrics to evaluate your call center performance. This includes:\n\n' +
        '- Total calls\n' +
        '- Average response time\n' +
        '- Average handle time\n' +
        '- Peak call time\n\n' +
        "I won't be able to answer anything beyond these metrics at this time.",
    );
    firstMessage.createdBy.type = CreatedByType.Assistant;

    this.bannerAccepted = this.hasAcceptedHeadsUpBanner();
  }

  handleConfirmHeadsUpBanner = () => {
    localStorage.setItem(
      MANAGER_ASSIST_LS_KEY.replace(HashRegex, this.userId),
      MANAGER_ASSIST_LS_ACCEPTED,
    );
    this.bannerAccepted = true;
  };

  handleSubmitMessage = async (messageText: string) => {
    if (!messageText.trim()) {
      return;
    }
    if (this.isLoading) {
      return;
    }
    this.errorMessage = null;
    this.isLoading = true;

    const newMessage = this.addMessage(messageText);

    if (!this.currentInteraction) {
      // Create interaction only once, when sending the first message.
      const interaction = await this.createInteraction();
      if (!interaction?.id) {
        this.isLoading = false;
        return;
      }
      this.currentInteraction = interaction;
      newMessage.interactionId = interaction.id;
    }

    const messageSent = await this.sendMessage(messageText);
    if (messageSent) {
      newMessage.id = messageSent.id;

      // start polling for new messages
      this.startPollingForNewMessages();
      return;
    }
    this.isLoading = false;
  };

  retry = async () => {
    if (!this.enableRetry) {
      return;
    }

    this.errorMessage = null;
    this.enableRetry = false;
    this.isLoading = true;
    this.startPollingForNewMessages();
  };

  dispose() {
    this.clearPolling();
  }

  private readonly hasAcceptedHeadsUpBanner = () => {
    const isAssistSubmitted = localStorage.getItem(
      MANAGER_ASSIST_LS_KEY.replace(HashRegex, this.userId),
    );
    return isAssistSubmitted === MANAGER_ASSIST_LS_ACCEPTED;
  };

  private readonly addMessage = (text: string) => {
    const newMessage = this.createMessageForUI(text);
    this.messages.push(newMessage);
    return newMessage;
  };

  private readonly createInteraction = async () => {
    const interactionData = new CreateInteractionRequest({
      createdBy: new CreatedBy({
        type: CreatedByType.User,
        id: this.userId,
      }),
      assistantId: this.assistantId,
    });

    try {
      const response =
        await this.interactionApi.createInteraction(interactionData);
      return response.data;
    } catch (error) {
      logger.error('Error creating interaction:', error);
      this.errorMessage = 'Sorry, there was an error creating the interaction.';
    }
  };

  private readonly sendMessage = async (text: string) => {
    if (!this.currentInteraction?.id || !text?.trim()) {
      return;
    }

    try {
      const newMessage = new CreateMessageRequest({
        targetRoleId: this.targetRoleId,
        assistantId: this.assistantId,
        type: MessageType.SimpleMessage,
        createdBy: new CreatedBy({
          type: CreatedByType.User,
          id: this.userId,
        }),
        details: new SimpleMessage(),
        suggestions: undefined,
        context: {
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        additionalContext: undefined,
      });
      newMessage.details.text = text;

      const response = await this.interactionApi.createMessage(
        this.currentInteraction.id,
        newMessage,
      );
      return response.data;
    } catch (error) {
      logger.error('Error sending message:', error);
      this.errorMessage = 'Sorry, there was an error sending your message.';
    }
  };

  private readonly getMessages = async (
    interactionId: string,
    page: number = 1,
    perPage: number = 50,
  ) => {
    try {
      // Calculate offset based on page and perPage
      const offset = (page - 1) * perPage;

      const response = await this.interactionApi.getMessages(
        this.targetRoleId,
        [interactionId], // interactionIds array
        undefined, // status - undefined for all
        undefined, // since - undefined for all time
        undefined, // type - undefined for all types
        offset,
        perPage,
      );

      return response.data;
    } catch (error) {
      logger.error('Error fetching conversation:', error);
      throw error;
    }
  };

  // Create a message object for UI (without sending to API)
  private readonly createMessageForUI = (text: string): Message => {
    const userMessage = new Message();
    // Generate temporary ID for tracking
    userMessage.id = uuIdV4();
    userMessage.type = MessageType.SimpleMessage;
    userMessage.interactionId = this.currentInteraction?.id ?? '0';
    userMessage.createdAt = new Date();
    userMessage.updatedAt = new Date();
    userMessage.createdBy = new CreatedBy();
    userMessage.createdBy.type = CreatedByType.User;
    userMessage.createdBy.id = this.userId;
    userMessage.details = new SimpleMessage();
    userMessage.details.text = text;

    return userMessage;
  };

  // Message polling methods
  private readonly startPollingForNewMessages = () => {
    if (!this.currentInteraction?.id) {
      return;
    }

    // Store initial message count
    this.initialMessageCount = this.messages.length;

    logger.info(
      'Starting polling for new messages, current count:',
      this.initialMessageCount,
    );

    // start polling immediately
    this.pollRetryCount = 0;
    this.pollForNewMessages(this.currentInteraction.id);
  };

  private readonly clearPolling = () => {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  };

  private readonly loadMessages = async (interactionId: string) => {
    try {
      const { messages } = await this.getMessages(interactionId);
      if (!messages) {
        return;
      }

      const existingIds = new Set(this.messages.map((m) => m.id));
      const newSimpleMessages = messages
        .filter(({ type }) => type === MessageType.SimpleMessage)
        .filter(({ id }) => !existingIds.has(id));
      this.messages.push(...newSimpleMessages);
    } catch (error) {
      logger.error('Error loading messages:', error);
      throw error;
    }
  };

  private readonly pollForNewMessages = async (interactionId: string) => {
    try {
      this.clearPolling();
      await this.loadMessages(interactionId);

      // Check if we got new messages
      if (this.messages.length > this.initialMessageCount) {
        this.isLoading = false;

        logger.info('New messages detected, stopping polling');
        logger.info('Stopped polling for new messages');
        return;
      }

      // No new messages yet, increment retry count - stop after max retries
      if (this.pollRetryCount >= this.pollMaxRetries) {
        this.enableRetry = true;
        this.isLoading = false;
        this.errorMessage =
          "It seems like I'm having trouble connecting to the service right now.  Give it a moment, or feel free to try again!";
        logger.warn(
          'Max polling retries reached, stopping polling',
          this.pollRetryCount,
        );
        logger.info('Stopped polling for new messages');
        return;
      }

      this.pollRetryCount++;

      this.pollTimer = setTimeout(async () => {
        await this.pollForNewMessages(interactionId);
      }, this.pollInterval);
    } catch (error) {
      this.enableRetry = true;
      this.isLoading = false;
      this.errorMessage =
        'Sorry, there was an error when polling for new messages.';
      logger.error('Error polling for new messages:', error);
      logger.info('Stopped polling for new messages');
    }
  };
}
